import React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import StudentTile from './StudentTile';

const countLabel = (n, singular, plural) => {
  const noun = n === 1 ? singular : (plural || `${singular}s`);
  return `${n} ${noun} available`;
};

const FacilitatorHome = ({
  caseCount = 0,
  spotErrorCount = 0,
  outpatientCount = 0,
  reviewPendingCount = 0,
  assignmentCount = 0,
  qbiCount = 0,
  showAdminTile = false,
  onCreateCaseStudy,
  onOpenCaseLibrary,
  onOpenQbiLibrary,
  onOpenAssignments,
  onOpenSpotErrorLibrary,
  onOpenOutpatientLibrary,
  onOpenEpma,
  onOpenReviewQueue,
  onOpenStudentPortfolios,
  onOpenStats,
  onOpenAdminSettings,
}) => {
  const showAdminSection = Boolean(onOpenStats) || showAdminTile || Boolean(onOpenReviewQueue) || Boolean(onOpenStudentPortfolios);
  return (
    <div className="student-page">
      <Container className="mt-4 student-page__content">
        <div className="student-dashboard-shell">
          <div className="student-dashboard-header">
            <div>
              <h2 className="mb-2">Facilitator workspace</h2>
              <p className="student-dashboard-header__copy mb-0">
                Author cases and exercises, then run them self-paced or live.
              </p>
            </div>
          </div>

          <h3 className="mt-2 mb-3">Case studies</h3>
          <Row className="g-4 mb-4">
            <Col xs={6} md={6} lg={4}>
              <StudentTile
                title="Create inpatient case"
                description="Build a new case: patient, chart, biochem, obs and questions."
                icon="bi bi-file-earmark-plus"
                eyebrow="Authoring"
                onClick={onCreateCaseStudy}
                variant="indigo"
              />
            </Col>
            <Col xs={6} md={6} lg={4}>
              <StudentTile
                title="Inpatient case library"
                description="Publish cases, run live classrooms and share join links."
                icon="bi bi-folder2-open"
                eyebrow={countLabel(caseCount, 'case')}
                onClick={onOpenCaseLibrary}
                variant="blue"
              />
            </Col>
            {onOpenOutpatientLibrary ? (
              <Col xs={6} md={6} lg={4}>
                <StudentTile
                  title="Outpatient cases"
                  description="Author clinic outpatient scenarios with library-picked answer keys. CD legal checks built in."
                  icon="bi bi-prescription2"
                  eyebrow={countLabel(outpatientCount, 'case study', 'case studies')}
                  onClick={onOpenOutpatientLibrary}
                  variant="emerald"
                />
              </Col>
            ) : null}
          </Row>

          <h3 className="mt-2 mb-3">Practice environment</h3>
          <Row className="g-4 mb-4">
            {onOpenEpma ? (
              <Col xs={6} md={6} lg={4}>
                <StudentTile
                  title="ePMA sandbox"
                  description="Admit a test patient and rehearse prescribing in the same simulator your students use. Useful for building case scripts and demoing live in lecture."
                  icon="bi bi-capsule-pill"
                  eyebrow="Same as student view"
                  onClick={onOpenEpma}
                  variant="indigo"
                />
              </Col>
            ) : null}
          </Row>

          <h3 className="mt-2 mb-3">Exercises</h3>
          <Row className="g-4 mb-4">
            {onOpenQbiLibrary ? (
              <Col xs={6} md={6} lg={4}>
                <StudentTile
                  title="Question library"
                  description="Author individual questions and compose them into named sets. One place for everything quiz-related."
                  icon="bi bi-collection"
                  eyebrow={qbiCount > 0 ? countLabel(qbiCount, 'question') : 'New • Build your bank'}
                  onClick={onOpenQbiLibrary}
                  variant="violet"
                />
              </Col>
            ) : null}
            {onOpenAssignments ? (
              <Col xs={6} md={6} lg={4}>
                <StudentTile
                  title="Assignments"
                  description="Schedule an exercise to a cohort with a release and due date. Track who's completed it and their scores."
                  icon="bi bi-calendar-check"
                  eyebrow={assignmentCount > 0 ? `${assignmentCount} scheduled` : 'New'}
                  onClick={onOpenAssignments}
                  variant="indigo"
                />
              </Col>
            ) : null}
            {onOpenSpotErrorLibrary ? (
              <Col xs={6} md={6} lg={4}>
                <StudentTile
                  title="Spot-the-error exercises"
                  description="Time-pressured chart-flagging. Graded on precision and recall."
                  icon="bi bi-exclamation-triangle"
                  eyebrow={countLabel(spotErrorCount, 'exercise')}
                  onClick={onOpenSpotErrorLibrary}
                  variant="rose"
                />
              </Col>
            ) : null}
          </Row>

          {showAdminSection ? (
            <>
              <h3 className="mt-2 mb-3">Educator admin</h3>
              <Row className="g-4 mb-4">
                {onOpenReviewQueue ? (
                  <Col xs={6} md={6} lg={4}>
                    <StudentTile
                      title="Lecturer review queue"
                      description="Submissions flagged for review. Add a comment and mark each one done."
                      icon="bi bi-clipboard2-check"
                      eyebrow={reviewPendingCount > 0 ? `${reviewPendingCount} pending` : 'No pending'}
                      onClick={onOpenReviewQueue}
                      variant="rose"
                    />
                  </Col>
                ) : null}
                {onOpenStudentPortfolios ? (
                  <Col xs={6} md={6} lg={4}>
                    <StudentTile
                      title="Student portfolios"
                      description="Browse each student's reflections, CPD log, and outcome tagging."
                      icon="bi bi-journal-bookmark"
                      onClick={onOpenStudentPortfolios}
                      variant="cyan"
                    />
                  </Col>
                ) : null}
                {onOpenStats ? (
                  <Col xs={6} md={6} lg={4}>
                    <StudentTile
                      title="Cohort analytics"
                      description="Engagement and outcomes per tenant. Date-filter, CSV export."
                      icon="bi bi-graph-up"
                      eyebrow="Educator-admin"
                      onClick={onOpenStats}
                      variant="violet"
                    />
                  </Col>
                ) : null}
                {showAdminTile ? (
                  <Col xs={6} md={6} lg={4}>
                    <StudentTile
                      title="Admin settings"
                      description="Manage routes, frequencies and the shared drug library."
                      icon="bi bi-sliders"
                      eyebrow="Facilitator-admin"
                      onClick={onOpenAdminSettings}
                      variant="slate"
                    />
                  </Col>
                ) : null}
              </Row>
            </>
          ) : null}

        </div>
      </Container>
    </div>
  );
};

export default FacilitatorHome;
